// Product-specific application sections. Keyed by the `block` string on
// productCatalog.js. Each entry returns a section object compatible with
// MORTGAGE_FORM_SECTIONS (id, title, fields[]). We keep them light and
// plain-English so they can be filled on a phone in 2–3 minutes.

const PROPERTY_PURCHASE_FIELDS = [
  { id: "address",         label: "Property Address",         type: "textarea", full: true },
  { id: "postcode",        label: "Postcode",                 type: "postcode" },
  { id: "purchase_price",  label: "Purchase Price",           type: "money" },
  { id: "loan_amount",     label: "Loan Amount",              type: "money" },
  { id: "deposit",         label: "Deposit",                  type: "money" },
  { id: "property_type",   label: "Property Type",            type: "text",   help: "e.g. detached house, flat, bungalow." },
  { id: "bedrooms",        label: "Number of Bedrooms",       type: "number" },
  { id: "year_built",      label: "Year Built",               type: "number" },
  { id: "lease_years",     label: "Lease Years Remaining",    type: "number" },
  { id: "service_charge",  label: "Service Charge (p.a.)",    type: "money" },
  { id: "ground_rent",     label: "Ground Rent (p.a.)",       type: "money" },
  { id: "epc_rating",      label: "EPC Rating",               type: "text" },
  { id: "notes",           label: "Anything the lender should know",       type: "textarea", full: true },
];

const BLOCKS = {
  // ── Residential ────────────────────────────────────────────────────────
  property_purchase: {
    id: "property",
    title: "Property to be Purchased",
    fields: PROPERTY_PURCHASE_FIELDS,
  },
  property_purchase_ftb: {
    id: "property",
    title: "Property to be Purchased",
    description: "A few extras we need for first-time buyers.",
    fields: [
      ...PROPERTY_PURCHASE_FIELDS,
      { id: "deposit_source", label: "Where is the deposit coming from?", type: "select", options: ["Savings", "Gift from family", "Help to Buy ISA", "Lifetime ISA (LISA)", "Inheritance", "Sale of other asset", "Mixed"] },
      { id: "deposit_source_notes", label: "Any extra detail on the deposit source", type: "textarea", full: true },
      { id: "scheme_used", label: "Government scheme used?", type: "select", options: ["None", "First Homes", "Mortgage Guarantee Scheme", "Shared Ownership", "Right to Buy", "Other"] },
      { id: "scheme_detail", label: "Scheme details (if any)", type: "text", full: true, showIf: { field: "scheme_used", equals: "Other" } },
    ],
  },
  shared_ownership: {
    id: "property",
    title: "Shared Ownership Property",
    fields: [
      ...PROPERTY_PURCHASE_FIELDS,
      { id: "housing_association", label: "Housing Association / Provider", type: "text", full: true },
      { id: "share_percent", label: "Share you are buying (%)", type: "number" },
      { id: "full_market_value", label: "Full Market Value of Property", type: "money" },
      { id: "monthly_rent", label: "Monthly Rent on Remaining Share", type: "money" },
      { id: "staircasing_plan", label: "Do you plan to staircase (buy a larger share) later?", type: "yesno" },
    ],
  },
  self_build: {
    id: "project",
    title: "Self-build Project",
    fields: [
      { id: "plot_address",    label: "Plot Address", type: "textarea", full: true },
      { id: "postcode",        label: "Postcode", type: "postcode" },
      { id: "plot_cost",       label: "Plot Cost / Current Value", type: "money" },
      { id: "build_cost",      label: "Estimated Build Cost", type: "money" },
      { id: "contingency",     label: "Contingency Budget", type: "money" },
      { id: "projected_end_value", label: "Projected Finished Value", type: "money" },
      { id: "build_type",      label: "Type of Build", type: "select", options: ["New build from scratch", "Knock down & rebuild", "Barn or building conversion", "Major extension / renovation"] },
      { id: "method",          label: "Construction Method", type: "text", help: "e.g. traditional brick & block, timber frame, SIPs." },
      { id: "planning_status", label: "Planning Permission Status", type: "select", options: ["Granted — detailed", "Granted — outline", "Submitted, awaiting", "Pre-application only", "Not required"] },
      { id: "warranty_provider", label: "Warranty Provider (if arranged)", type: "text" },
      { id: "architect",       label: "Architect / Designer", type: "text" },
      { id: "contractor",      label: "Main Contractor (if any)", type: "text" },
      { id: "start_date",      label: "Expected Start Date", type: "date" },
      { id: "completion_date", label: "Expected Completion Date", type: "date" },
      { id: "notes",           label: "Project summary", type: "textarea", full: true },
    ],
  },
  equity_release: {
    id: "equity_release",
    title: "Equity Release Details",
    fields: [
      { id: "property_address", label: "Property Address", type: "textarea", full: true },
      { id: "property_value",   label: "Estimated Property Value", type: "money" },
      { id: "outstanding_mortgage", label: "Outstanding Mortgage (if any)", type: "money" },
      { id: "amount_wanted",    label: "Amount You Would Like to Release", type: "money" },
      { id: "plan_type",        label: "Preferred Plan Type", type: "select", options: ["Lump sum lifetime mortgage", "Drawdown lifetime mortgage", "Home reversion plan", "Not sure — advise me"] },
      { id: "purpose",          label: "What will the money be used for?", type: "textarea", full: true, help: "e.g. home improvements, gifting to family, clearing an existing mortgage." },
      { id: "advice_received",  label: "Have you discussed this with family?", type: "yesno" },
      { id: "existing_benefits", label: "Do you currently receive any means-tested benefits?", type: "yesno" },
    ],
  },

  // ── Refinance & further borrowing ─────────────────────────────────────
  remortgage: {
    id: "current_mortgage",
    title: "Current Mortgage Details",
    description: "Complete this section only where applicable.",
    fields: [
      { id: "lender",              label: "Lender", type: "text" },
      { id: "owners",              label: "Owner(s)", type: "text" },
      { id: "property_address",    label: "Property Address", type: "textarea", full: true },
      { id: "loan_outstanding",    label: "Loan Outstanding", type: "money" },
      { id: "term_remaining",      label: "Term Remaining", type: "text", help: "e.g. 18 years 4 months." },
      { id: "buy_to_let",          label: "Buy to Let", type: "yesno" },
      { id: "current_rate",        label: "Current Interest Rate (%)", type: "number" },
      { id: "monthly_payment",     label: "Monthly Mortgage Payment", type: "money" },
      { id: "account_number",      label: "Account Number", type: "text" },
      { id: "to_be_redeemed",      label: "To be Redeemed?", type: "yesno" },
      { id: "rate_type",           label: "Interest Rate Type", type: "text", help: "e.g. 5-year fixed, tracker, SVR." },
      { id: "rate_end_date",       label: "Current Rate End Date", type: "date" },
      { id: "second_charge",       label: "Second Charge?", type: "yesno" },
      { id: "early_repayment_penalties", label: "Early Repayment / Transfer Penalties", type: "textarea", full: true },
      { id: "erc_end_date",        label: "ERC End Date", type: "date" },
      { id: "property_value",      label: "Property Value / Sale Price", type: "money" },
      { id: "portable",            label: "Portable to New Property?", type: "select", options: ["Yes", "No", "Unknown"] },
      { id: "repayment_method",    label: "Repayment Method", type: "text", help: "e.g. capital & interest, interest-only." },
      { id: "capital_raise",       label: "Would you like to raise additional capital?", type: "yesno" },
      { id: "capital_raise_amount", label: "Additional capital wanted", type: "money", showIf: { field: "capital_raise", equals: "Yes" } },
      { id: "capital_raise_purpose", label: "Purpose of the extra borrowing", type: "textarea", full: true, showIf: { field: "capital_raise", equals: "Yes" } },
      { id: "notes",               label: "Notes", type: "textarea", full: true },
    ],
  },
  further_advance: {
    id: "further_advance",
    title: "Further Advance Details",
    fields: [
      { id: "current_lender",   label: "Current Lender", type: "text" },
      { id: "property_address", label: "Mortgaged Property Address", type: "textarea", full: true },
      { id: "property_value",   label: "Estimated Property Value", type: "money" },
      { id: "balance_outstanding", label: "Current Mortgage Balance", type: "money" },
      { id: "amount_wanted",    label: "Amount of Further Advance Wanted", type: "money" },
      { id: "term_remaining",   label: "Term Remaining on Main Mortgage", type: "text", help: "e.g. 20 years." },
      { id: "purpose",          label: "Purpose of the Further Advance", type: "textarea", full: true, help: "e.g. home improvements, debt consolidation, deposit for a BTL." },
      { id: "monthly_payment",  label: "Current Monthly Payment", type: "money" },
    ],
  },
  let_to_buy: {
    id: "let_to_buy",
    title: "Let-to-Buy Plan",
    description: "You keep your current home and let it out, and buy a new one to live in.",
    fields: [
      { id: "current_property_address", label: "Current Home (to be let)", type: "textarea", full: true },
      { id: "current_value",     label: "Current Value", type: "money" },
      { id: "current_balance",   label: "Current Mortgage Balance", type: "money" },
      { id: "expected_rent",     label: "Expected Monthly Rent", type: "money" },
      { id: "new_home_address",  label: "New Home Address (to be bought)", type: "textarea", full: true },
      { id: "new_purchase_price", label: "New Home Purchase Price", type: "money" },
      { id: "new_deposit",       label: "Deposit for New Home", type: "money" },
      { id: "timing",            label: "Target completion date", type: "date" },
    ],
  },
  second_charge: {
    id: "second_charge",
    title: "Second Charge Loan",
    fields: [
      { id: "property_address",  label: "Property Securing the Loan", type: "textarea", full: true },
      { id: "property_value",    label: "Estimated Property Value", type: "money" },
      { id: "first_charge_lender", label: "First Charge Lender", type: "text" },
      { id: "first_charge_balance", label: "First Charge Balance", type: "money" },
      { id: "first_charge_rate", label: "First Charge Interest Rate (%)", type: "number" },
      { id: "amount_wanted",     label: "Amount Needed", type: "money" },
      { id: "term",              label: "Term Needed (years)", type: "number" },
      { id: "purpose",           label: "What is the loan for?", type: "textarea", full: true },
    ],
  },

  // ── BTL family ────────────────────────────────────────────────────────
  btl: {
    id: "btl",
    title: "Buy-to-let Property",
    fields: [
      { id: "address",         label: "Property Address", type: "textarea", full: true },
      { id: "postcode",        label: "Postcode", type: "postcode" },
      { id: "purchase_price",  label: "Purchase Price / Value", type: "money" },
      { id: "loan_amount",     label: "Loan Amount Wanted", type: "money" },
      { id: "expected_rent",   label: "Expected Monthly Rent", type: "money" },
      { id: "property_type",   label: "Property Type", type: "text", help: "e.g. 2-bed flat, 3-bed terrace." },
      { id: "tenant_type",     label: "Tenant Profile", type: "select", options: ["Professional working tenants", "Family", "Students", "DSS / Housing Benefit", "Short-term lets / Airbnb", "Mixed"] },
      { id: "portfolio_size",  label: "How many rental properties do you already own?", type: "number" },
      { id: "portfolio_detail", label: "Short overview of existing portfolio (if any)", type: "textarea", full: true },
      { id: "property_held_as", label: "Will you own in?", type: "select", options: ["Personal name", "Limited company", "Partnership", "Not sure"] },
      { id: "notes",           label: "Anything we should know", type: "textarea", full: true },
    ],
  },
  ltd_co_btl: {
    id: "btl",
    title: "Limited Company Buy-to-let",
    description: "Property details first, then quick company info.",
    fields: [
      { id: "address",         label: "Property Address", type: "textarea", full: true },
      { id: "postcode",        label: "Postcode", type: "postcode" },
      { id: "purchase_price",  label: "Purchase Price / Value", type: "money" },
      { id: "loan_amount",     label: "Loan Amount Wanted", type: "money" },
      { id: "expected_rent",   label: "Expected Monthly Rent", type: "money" },
      { id: "tenant_type",     label: "Tenant Profile", type: "select", options: ["Professional working tenants", "Family", "Students", "DSS / Housing Benefit", "Short-term lets / Airbnb", "Mixed"] },
      { id: "company_name",    label: "Limited Company Name", type: "text", full: true },
      { id: "company_number",  label: "Companies House Number", type: "text" },
      { id: "company_sic",     label: "SIC Code (if known)", type: "text", help: "SPVs are usually 68209 or 68100." },
      { id: "company_type",    label: "Is this an SPV (property-only company)?", type: "yesno" },
      { id: "directors",       label: "Directors / Shareholders", type: "textarea", full: true, help: "Name, % share and date of birth for each." },
      { id: "trading_since",   label: "Company Started Trading", type: "date" },
      { id: "portfolio_size",  label: "How many properties does the company already own?", type: "number" },
    ],
  },
  holiday_let: {
    id: "btl",
    title: "Holiday Let Property",
    fields: [
      { id: "address",         label: "Property Address", type: "textarea", full: true },
      { id: "postcode",        label: "Postcode", type: "postcode" },
      { id: "purchase_price",  label: "Purchase Price / Value", type: "money" },
      { id: "loan_amount",     label: "Loan Amount Wanted", type: "money" },
      { id: "property_type",   label: "Property Type", type: "text" },
      { id: "weekly_rate_low", label: "Weekly Rate — Low Season", type: "money" },
      { id: "weekly_rate_mid", label: "Weekly Rate — Mid Season", type: "money" },
      { id: "weekly_rate_high", label: "Weekly Rate — High Season", type: "money" },
      { id: "occupancy_weeks", label: "Expected Occupied Weeks per Year", type: "number" },
      { id: "existing_short_let_income", label: "Current annual short-let income (if the property is already running)", type: "money" },
      { id: "booking_platforms", label: "Booking Platforms Used", type: "text", help: "e.g. Airbnb, Booking.com, Vrbo, direct." },
      { id: "management",      label: "Who will manage the property?", type: "select", options: ["I will manage it", "Family member", "Local agent", "Nationwide holiday-let company"] },
    ],
  },
  hmo_multi_unit: {
    id: "btl",
    title: "HMO / Multi-unit Property",
    fields: [
      { id: "address",         label: "Property Address", type: "textarea", full: true },
      { id: "postcode",        label: "Postcode", type: "postcode" },
      { id: "purchase_price",  label: "Purchase Price / Value", type: "money" },
      { id: "loan_amount",     label: "Loan Amount Wanted", type: "money" },
      { id: "number_of_rooms", label: "Number of Lettable Rooms", type: "number" },
      { id: "number_of_units", label: "Number of Self-contained Units (if multi-unit)", type: "number" },
      { id: "expected_rent_total", label: "Expected Total Monthly Rent", type: "money" },
      { id: "hmo_licence_status", label: "HMO Licence Status", type: "select", options: ["Granted", "Applied for", "Not needed (small HMO)", "Not sure"] },
      { id: "article_4",       label: "Is the property in an Article 4 area?", type: "select", options: ["Yes", "No", "Not sure"] },
      { id: "tenant_type",     label: "Tenant Profile", type: "select", options: ["Professionals", "Students", "DSS / Housing Benefit", "Mixed"] },
      { id: "layout_notes",    label: "Short description of layout", type: "textarea", full: true },
    ],
  },

  // ── Bridging & Development ────────────────────────────────────────────
  bridging: {
    id: "bridging",
    title: "Bridging Loan",
    fields: [
      { id: "security_address", label: "Security Property Address", type: "textarea", full: true },
      { id: "postcode",         label: "Postcode", type: "postcode" },
      { id: "property_value",   label: "Current Property Value", type: "money" },
      { id: "amount_wanted",    label: "Loan Amount Wanted", type: "money" },
      { id: "term_months",      label: "Loan Term Needed (months)", type: "number" },
      { id: "purpose",          label: "What is the loan for?", type: "textarea", full: true },
      { id: "exit_plan",        label: "Exit Plan", type: "select", options: ["Sale of security property", "Refinance to a term mortgage", "Sale of another property", "Business proceeds / lump sum", "Inheritance / investment maturity", "Other"] },
      { id: "exit_detail",      label: "Exit plan details", type: "textarea", full: true },
      { id: "first_charge_existing", label: "Existing mortgage on this property?", type: "yesno" },
      { id: "first_charge_balance",  label: "Existing mortgage balance", type: "money", showIf: { field: "first_charge_existing", equals: "Yes" } },
      { id: "additional_security", label: "Any additional property offered as security?", type: "textarea", full: true },
      { id: "borrower_type",    label: "Who is borrowing?", type: "select", options: ["Personal name", "Limited company / SPV", "Partnership / Trust"] },
    ],
  },
  auction_finance: {
    id: "auction",
    title: "Auction Finance",
    fields: [
      { id: "auction_house",    label: "Auction House", type: "text" },
      { id: "auction_date",     label: "Auction Date", type: "date" },
      { id: "lot_number",       label: "Lot Number", type: "text" },
      { id: "property_address", label: "Property Address", type: "textarea", full: true },
      { id: "postcode",         label: "Postcode", type: "postcode" },
      { id: "guide_price",      label: "Guide Price", type: "money" },
      { id: "max_bid",          label: "Maximum Bid You Would Pay", type: "money" },
      { id: "deposit_available", label: "Deposit Funds Available", type: "money" },
      { id: "completion_days",  label: "Completion Window (days)", type: "number", help: "Usually 28 days for a traditional auction." },
      { id: "intended_use",     label: "Planned Use for the Property", type: "select", options: ["Buy-to-let", "Refurb and sell", "Refurb and let", "Owner-occupier", "Development"] },
      { id: "exit_plan",        label: "Exit Plan", type: "textarea", full: true },
    ],
  },
  development: {
    id: "development",
    title: "Development Project",
    fields: [
      { id: "site_address",     label: "Site Address", type: "textarea", full: true },
      { id: "postcode",         label: "Postcode", type: "postcode" },
      { id: "site_value_current", label: "Current Site Value", type: "money" },
      { id: "purchase_price",   label: "Purchase Price (if buying now)", type: "money" },
      { id: "build_cost",       label: "Estimated Build Cost", type: "money" },
      { id: "contingency",      label: "Contingency Budget", type: "money" },
      { id: "gdv",              label: "Gross Development Value (GDV)", type: "money" },
      { id: "number_of_units",  label: "Number of Units to Be Built / Converted", type: "number" },
      { id: "project_type",     label: "Project Type", type: "select", options: ["Ground-up new build", "Conversion", "Permitted development", "Extension & reconfiguration", "Mixed-use"] },
      { id: "planning_status",  label: "Planning Status", type: "select", options: ["Full planning granted", "Outline planning granted", "Pre-application only", "Not submitted"] },
      { id: "planning_reference", label: "Planning Reference", type: "text" },
      { id: "start_date",       label: "Expected Start on Site", type: "date" },
      { id: "duration_months",  label: "Build Duration (months)", type: "number" },
      { id: "contractor",       label: "Main Contractor", type: "text" },
      { id: "experience",       label: "Previous development experience", type: "textarea", full: true, help: "List 2–3 recent schemes with addresses, GDV and outcome." },
      { id: "exit_plan",        label: "Exit Plan", type: "select", options: ["Sell all units", "Sell some, keep some", "Refinance to BTL / Dev-exit", "Mixed"] },
      { id: "exit_detail",      label: "Exit plan detail", type: "textarea", full: true },
    ],
  },
  development_exit: {
    id: "development_exit",
    title: "Development Exit",
    fields: [
      { id: "site_address",     label: "Scheme Address", type: "textarea", full: true },
      { id: "postcode",         label: "Postcode", type: "postcode" },
      { id: "scheme_description", label: "Short Scheme Description", type: "textarea", full: true },
      { id: "current_lender",   label: "Current Development Lender", type: "text" },
      { id: "current_balance",  label: "Current Loan Balance", type: "money" },
      { id: "amount_wanted",    label: "Development Exit Loan Amount", type: "money" },
      { id: "gdv",              label: "Gross Development Value (GDV)", type: "money" },
      { id: "units_total",      label: "Total Units in Scheme", type: "number" },
      { id: "units_sold",       label: "Units Sold or Reserved", type: "number" },
      { id: "practical_completion", label: "Practical Completion Date", type: "date" },
      { id: "sales_strategy",   label: "Sales Strategy", type: "textarea", full: true },
    ],
  },
  refurb: {
    id: "refurb",
    title: "Refurbishment Project",
    fields: [
      { id: "property_address", label: "Property Address", type: "textarea", full: true },
      { id: "postcode",         label: "Postcode", type: "postcode" },
      { id: "property_value_now", label: "Current Property Value", type: "money" },
      { id: "purchase_price",   label: "Purchase Price (if buying now)", type: "money" },
      { id: "refurb_cost",      label: "Estimated Refurbishment Cost", type: "money" },
      { id: "contingency",      label: "Contingency Budget", type: "money" },
      { id: "post_works_value", label: "End Value after Works", type: "money" },
      { id: "scope",            label: "Scope of Works", type: "textarea", full: true, help: "e.g. kitchen & bathrooms, re-wire, loft conversion, extension." },
      { id: "planning_needed",  label: "Does the project need planning?", type: "yesno" },
      { id: "planning_status",  label: "Planning status (if required)", type: "text", full: true, showIf: { field: "planning_needed", equals: "Yes" } },
      { id: "duration_weeks",   label: "Expected Duration (weeks)", type: "number" },
      { id: "exit_plan",        label: "Exit Plan", type: "select", options: ["Sell", "Refinance to BTL", "Refinance to residential mortgage", "Keep and let"] },
      { id: "experience",       label: "Previous refurb experience", type: "textarea", full: true },
    ],
  },

  // ── Commercial ────────────────────────────────────────────────────────
  commercial_property: {
    id: "commercial_property",
    title: "Commercial Property",
    fields: [
      { id: "address",          label: "Property Address", type: "textarea", full: true },
      { id: "postcode",         label: "Postcode", type: "postcode" },
      { id: "property_use",     label: "Property Use", type: "select", options: ["Office", "Retail / high street", "Industrial / warehouse", "Hospitality (pub, hotel, café)", "Healthcare / care home", "Leisure", "Other"] },
      { id: "property_use_other", label: "Describe use (if other)", type: "text", full: true, showIf: { field: "property_use", equals: "Other" } },
      { id: "value",            label: "Purchase Price / Value", type: "money" },
      { id: "loan_amount",      label: "Loan Amount Wanted", type: "money" },
      { id: "term_years",       label: "Term Wanted (years)", type: "number" },
      { id: "owner_occupier",   label: "Will you (or your company) trade from this property?", type: "yesno" },
      { id: "tenant_name",      label: "Tenant Name (if let)", type: "text" },
      { id: "annual_rent",      label: "Annual Rent Received (if let)", type: "money" },
      { id: "lease_remaining",  label: "Years Remaining on Lease", type: "number" },
      { id: "business_summary", label: "If owner-occupied — short business summary", type: "textarea", full: true },
    ],
  },
  semi_commercial: {
    id: "semi_commercial",
    title: "Semi-Commercial Property",
    description: "Mix of commercial and residential, e.g. shop with a flat above.",
    fields: [
      { id: "address",             label: "Property Address", type: "textarea", full: true },
      { id: "postcode",            label: "Postcode", type: "postcode" },
      { id: "value",               label: "Purchase Price / Value", type: "money" },
      { id: "loan_amount",         label: "Loan Amount Wanted", type: "money" },
      { id: "commercial_use",      label: "Commercial Use", type: "text", help: "e.g. retail unit, takeaway, office." },
      { id: "commercial_rent",     label: "Commercial Monthly Rent", type: "money" },
      { id: "commercial_lease",    label: "Commercial Lease Years Remaining", type: "number" },
      { id: "residential_units",   label: "Number of Residential Units", type: "number" },
      { id: "residential_rent",    label: "Total Residential Monthly Rent", type: "money" },
      { id: "split_percent",       label: "Approximate commercial vs residential value split (%)", type: "text", help: "e.g. 40% commercial / 60% residential." },
    ],
  },

  // ── Business ──────────────────────────────────────────────────────────
  business_loan: {
    id: "business_loan",
    title: "Business & Loan Details",
    fields: [
      { id: "business_name",      label: "Business Name", type: "text", full: true },
      { id: "trading_name",       label: "Trading Name (if different)", type: "text" },
      { id: "company_number",     label: "Companies House Number", type: "text" },
      { id: "legal_form",         label: "Legal Form", type: "select", options: ["Sole trader", "Partnership", "Limited company", "LLP", "Not-for-profit"] },
      { id: "sic_code",           label: "SIC / Industry", type: "text" },
      { id: "trading_since",      label: "Trading Since", type: "date" },
      { id: "registered_address", label: "Registered Address", type: "textarea", full: true },
      { id: "trading_address",    label: "Main Trading Address (if different)", type: "textarea", full: true },
      { id: "staff_count",        label: "Number of Employees (incl. owners)", type: "number" },
      { id: "turnover_last_year", label: "Turnover — Last Year", type: "money" },
      { id: "net_profit_last_year", label: "Net Profit — Last Year", type: "money" },
      { id: "turnover_current",   label: "Projected Turnover — This Year", type: "money" },
      { id: "amount_wanted",      label: "Loan Amount Wanted", type: "money" },
      { id: "term_months",        label: "Term Wanted (months)", type: "number" },
      { id: "purpose",            label: "What is the loan for?", type: "textarea", full: true },
      { id: "director_guarantees", label: "Are directors willing to give personal guarantees?", type: "yesno" },
      { id: "security_offered",   label: "Security Offered (if any)", type: "textarea", full: true },
      { id: "existing_lending",   label: "Existing Business Lending", type: "textarea", full: true },
    ],
  },
  asset_finance: {
    id: "asset_finance",
    title: "Asset Finance Details",
    fields: [
      { id: "asset_type",       label: "Type of Asset", type: "select", options: ["Vehicle", "Plant & machinery", "Office equipment / IT", "Agricultural", "Catering", "Manufacturing equipment", "Other"] },
      { id: "asset_description", label: "Full Description", type: "textarea", full: true, help: "Make, model, year, condition." },
      { id: "new_or_used",      label: "New or Used?", type: "select", options: ["New", "Used"] },
      { id: "supplier",         label: "Supplier Name", type: "text" },
      { id: "cost_ex_vat",      label: "Cost (excl. VAT)", type: "money" },
      { id: "vat",              label: "VAT", type: "money" },
      { id: "deposit",          label: "Deposit You Will Put Down", type: "money" },
      { id: "term_months",      label: "Term Wanted (months)", type: "number" },
      { id: "balloon",          label: "Balloon / Final Payment Wanted?", type: "yesno" },
      { id: "finance_type",     label: "Preferred Finance Type", type: "select", options: ["Hire Purchase", "Finance Lease", "Operating Lease", "Not sure"] },
      { id: "business_name",    label: "Business Name", type: "text", full: true },
      { id: "trading_since",    label: "Trading Since", type: "date" },
      { id: "purpose",          label: "How will the asset help the business?", type: "textarea", full: true },
    ],
  },
  invoice_finance: {
    id: "invoice_finance",
    title: "Invoice Finance Details",
    fields: [
      { id: "business_name",     label: "Business Name", type: "text", full: true },
      { id: "legal_form",        label: "Legal Form", type: "select", options: ["Sole trader", "Partnership", "Limited company", "LLP"] },
      { id: "sector",            label: "Industry / Sector", type: "text" },
      { id: "annual_turnover",   label: "Annual Turnover", type: "money" },
      { id: "monthly_invoiced",  label: "Average Monthly Invoiced to Business Customers", type: "money" },
      { id: "number_of_customers", label: "Number of Regular Customers", type: "number" },
      { id: "payment_terms",     label: "Standard Payment Terms", type: "text", help: "e.g. 30 days from invoice." },
      { id: "typical_days_paid", label: "Typical Days Taken to Get Paid", type: "number" },
      { id: "funding_type",      label: "Facility Type Wanted", type: "select", options: ["Confidential invoice discounting", "Factoring", "Selective / spot factoring", "Not sure"] },
      { id: "advance_rate_target", label: "Advance Rate You'd Like (% of invoice)", type: "number" },
      { id: "existing_facility", label: "Any existing invoice finance facility?", type: "textarea", full: true },
      { id: "bad_debts",         label: "Bad debts in the last 12 months?", type: "yesno" },
    ],
  },
  mca: {
    id: "mca",
    title: "Merchant Cash Advance",
    fields: [
      { id: "business_name",     label: "Business Name", type: "text", full: true },
      { id: "trading_since",     label: "Trading Since", type: "date" },
      { id: "legal_form",        label: "Legal Form", type: "select", options: ["Sole trader", "Partnership", "Limited company", "LLP"] },
      { id: "monthly_card_takings", label: "Average Monthly Card Takings", type: "money" },
      { id: "card_terminal_provider", label: "Card Terminal / Payment Provider", type: "text", help: "e.g. Worldpay, Stripe, SumUp, Zettle." },
      { id: "business_bank",     label: "Business Bank", type: "text" },
      { id: "amount_wanted",     label: "Amount Wanted", type: "money" },
      { id: "repayment_percent", label: "Preferred Repayment % of daily card takings", type: "number" },
      { id: "purpose",           label: "What will the funds be used for?", type: "textarea", full: true },
    ],
  },
  working_capital: {
    id: "working_capital",
    title: "Working Capital / Overdraft",
    fields: [
      { id: "business_name",     label: "Business Name", type: "text", full: true },
      { id: "legal_form",        label: "Legal Form", type: "select", options: ["Sole trader", "Partnership", "Limited company", "LLP"] },
      { id: "trading_since",     label: "Trading Since", type: "date" },
      { id: "annual_turnover",   label: "Annual Turnover", type: "money" },
      { id: "monthly_turnover",  label: "Average Monthly Turnover", type: "money" },
      { id: "monthly_profit",    label: "Average Monthly Profit", type: "money" },
      { id: "facility_type",     label: "Preferred Facility", type: "select", options: ["Business overdraft", "Revolving credit facility", "Short-term unsecured loan", "Not sure"] },
      { id: "amount_wanted",     label: "Facility Size Wanted", type: "money" },
      { id: "term_months",       label: "Term Wanted (months)", type: "number" },
      { id: "purpose",           label: "Purpose / Cashflow context", type: "textarea", full: true },
      { id: "director_guarantees", label: "Are directors willing to give personal guarantees?", type: "yesno" },
    ],
  },
  commercial_vehicle: {
    id: "commercial_vehicle",
    title: "Commercial Vehicle Finance",
    fields: [
      { id: "vehicle_type",      label: "Type of Vehicle", type: "select", options: ["Van (light commercial)", "HGV / LGV", "Minibus / Coach", "Taxi / Private hire", "Specialist (recovery, tipper, refrigerated)"] },
      { id: "make_model",        label: "Make & Model", type: "text" },
      { id: "year",              label: "Year of Registration", type: "number" },
      { id: "new_or_used",       label: "New or Used?", type: "select", options: ["New", "Used"] },
      { id: "mileage",           label: "Mileage (if used)", type: "number" },
      { id: "cost_ex_vat",       label: "Cost (excl. VAT)", type: "money" },
      { id: "deposit",           label: "Deposit", type: "money" },
      { id: "term_months",       label: "Term Wanted (months)", type: "number" },
      { id: "finance_type",      label: "Preferred Finance Type", type: "select", options: ["Hire Purchase", "Contract Hire", "Finance Lease", "Not sure"] },
      { id: "business_name",     label: "Business Name", type: "text", full: true },
      { id: "trading_since",     label: "Trading Since", type: "date" },
      { id: "annual_mileage",    label: "Expected Annual Mileage", type: "number" },
      { id: "use",               label: "Business use for the vehicle", type: "textarea", full: true },
    ],
  },

  // ── Personal ──────────────────────────────────────────────────────────
  car_finance: {
    id: "car_finance",
    title: "Vehicle & Finance Details",
    fields: [
      { id: "make_model",        label: "Make & Model", type: "text" },
      { id: "year",              label: "Year of Registration", type: "number" },
      { id: "mileage",           label: "Current Mileage", type: "number" },
      { id: "new_or_used",       label: "New or Used?", type: "select", options: ["New", "Used"] },
      { id: "fuel_type",         label: "Fuel Type", type: "select", options: ["Petrol", "Diesel", "Hybrid", "Electric", "Other"] },
      { id: "price",             label: "Price", type: "money" },
      { id: "deposit",           label: "Deposit", type: "money" },
      { id: "term_months",       label: "Term Wanted (months)", type: "number" },
      { id: "annual_mileage",    label: "Expected Annual Mileage", type: "number" },
      { id: "dealer_name",       label: "Dealer / Seller Name", type: "text" },
      { id: "purpose",           label: "Vehicle use", type: "select", options: ["Personal only", "Mixed personal & work", "Mostly business"] },
    ],
  },
  personal_loan: {
    id: "personal_loan",
    title: "Loan Details",
    fields: [
      { id: "amount_wanted",     label: "Amount Wanted", type: "money" },
      { id: "term_months",       label: "Term Wanted (months)", type: "number" },
      { id: "purpose",           label: "What is the loan for?", type: "select", options: ["Home improvements", "Car / vehicle", "Wedding or special event", "Holiday", "Medical / dental", "Pay off other credit", "Deposit", "Other"] },
      { id: "purpose_detail",    label: "Purpose details", type: "textarea", full: true },
      { id: "existing_loans",    label: "Any existing loans with similar lenders?", type: "textarea", full: true },
    ],
  },
  debt_consolidation: {
    id: "debt_consolidation",
    title: "Debt Consolidation",
    fields: [
      { id: "amount_wanted",     label: "Total Loan Needed", type: "money" },
      { id: "term_months",       label: "Term Wanted (months)", type: "number" },
      { id: "debts_summary",     label: "Debts to be Consolidated", type: "textarea", full: true, help: "List each lender, balance, monthly payment and interest rate if known." },
      { id: "total_monthly_now", label: "Total Monthly Repayments Today", type: "money" },
      { id: "reason",            label: "Why consolidate now?", type: "textarea", full: true, help: "e.g. lower monthly payment, single simple payment, pay off faster." },
      { id: "close_accounts",    label: "Do you intend to close the credit accounts after settling them?", type: "yesno" },
    ],
  },
  credit_card: {
    id: "credit_card",
    title: "Credit Card / Credit Product",
    fields: [
      { id: "product_type",      label: "Product Type", type: "select", options: ["Credit card", "Store card", "Catalogue", "Overdraft", "Buy-now-pay-later"] },
      { id: "credit_limit_wanted", label: "Credit Limit Wanted", type: "money" },
      { id: "purpose",           label: "Main reason for applying", type: "select", options: ["Everyday spending", "Balance transfer", "Building credit", "Large one-off purchase", "Travel / overseas", "Business expenses"] },
      { id: "monthly_spend_estimate", label: "Expected Monthly Spend", type: "money" },
      { id: "repay_in_full",     label: "Will you repay the balance in full each month?", type: "yesno" },
      { id: "existing_cards",    label: "Existing credit cards / overdrafts (lender & limit)", type: "textarea", full: true },
    ],
  },
};

export const getProductBlock = (block) => BLOCKS[block] || null;
